import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { gql, useMutation } from '@apollo/client';
import { difference } from 'lodash';
import {
  ContactTag,
  Mutation,
  MutationAddContactTagArgs,
  MutationRemoveContactTagArgs,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { ContactTagRefetchType } from 'src/components/domain/contacts/hooks/use-contact-tags';
import { ContactTagSelect } from 'src/components/pages/contacts/components/contact-tag-select';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';

const ADD_CONTACT_TAG = gql`
  mutation AddContactTag($input: AddContactTagInput!) {
    addContactTag(input: $input) {
      contact {
        id
        tags {
          id
          name
        }
      }
    }
  }
`;

const REMOVE_CONTACT_TAG = gql`
  mutation RemoveContactTag($input: RemoveContactTagInput!) {
    removeContactTag(input: $input) {
      contact {
        id
        tags {
          id
          name
        }
      }
    }
  }
`;

type TagsSelectorProps = {
  contactId: number;
  selectedTags: ContactTag[];
};

export const LegacyContactTagsSelector: React.FC<TagsSelectorProps> = ({
  contactId,
  selectedTags,
  ...rest
}) => {
  const { id: institutionId } = useCurrentInstitution();
  const handleGraphQLError = useHandleGraphQLError();
  const { t } = useTranslation(['contacts']);
  const [loading, setLoading] = useState(false);
  const refetchRef = useRef<ContactTagRefetchType | null>(null);
  const [addContactTag] = useMutation<
    Pick<Mutation, 'addContactTag'>,
    MutationAddContactTagArgs
  >(ADD_CONTACT_TAG);
  const [removeContactTag] = useMutation<
    Pick<Mutation, 'removeContactTag'>,
    MutationRemoveContactTagArgs
  >(REMOVE_CONTACT_TAG);

  const handleChange = useCallback(
    async (existingTagIds: number[], newTagNames: string[]) => {
      const selectedTagIds = selectedTags.map((tag) => tag.id);

      let addTagRequest: null | (() => Promise<void>) = null;
      let removeTagRequest: null | (() => Promise<void>) = null;

      const tagsToAdd = [
        ...difference(existingTagIds, selectedTagIds).map((id) => id.toString()),
        ...newTagNames.map((name) => `new-tag-${name}`),
      ];

      if (tagsToAdd.length) {
        addTagRequest = async () => {
          await addContactTag({
            variables: {
              input: {
                contactId,
                tags: tagsToAdd,
                institutionId,
              },
            },
            onError: handleGraphQLError,
          });
        };
      }

      const tagsToRemove = difference(selectedTagIds, existingTagIds);

      if (tagsToRemove.length) {
        removeTagRequest = async () => {
          await removeContactTag({
            variables: {
              input: {
                contactId,
                tagsId: tagsToRemove,
                institutionId,
              },
            },
            onError: handleGraphQLError,
          });
        };
      }

      if (addTagRequest || removeTagRequest) {
        setLoading(true);
        await addTagRequest?.();
        newTagNames.length && (await refetchRef.current?.()); // Makes created tags show in the list
        await removeTagRequest?.();
        setLoading(false);
      }
    },
    [
      addContactTag,
      contactId,
      handleGraphQLError,
      institutionId,
      removeContactTag,
      selectedTags,
    ],
  );

  return (
    <ContactTagSelect
      {...rest}
      isMulti
      name="contact-details-tags"
      placeholder={t('contacts:information.tags')}
      mobileLabel={t('contacts:information.tags')}
      isLoading={loading}
      isCreatable
      refetchRef={(refetch) => (refetchRef.current = refetch)}
      selectedTagsIds={selectedTags.map((tag) => tag.id)}
      onChange={handleChange}
    />
  );
};
